import { CustomAppEvents } from 'core/common/entities';
import { checkIsClientSide } from 'core/common/utils/checkIsClientSide';
import { CookieConsentService } from 'core/cookie-consent/interfaces';
import { AnalyticsPixelId, TaboolaEventName, TaboolaMethods } from '../../entities';
import { TaboolaService } from '../../interfaces';

const noop: TaboolaMethods & { isAvailable: false } = {
  isAvailable: false as const,
  revokeConsentEu: () => {},
  revokeConsentUs: () => {},
  grantConsentEu: () => {},
  grantConsentUs: () => {},
  track: () => {},
};

export class HttpTaboolaService implements TaboolaService {
  private readonly passedPixelIds: Array<AnalyticsPixelId> = [];
  private initializedPixelIds: Array<AnalyticsPixelId> = [];

  constructor(
    private readonly cookieConsentService: CookieConsentService,
    pixelIds?: Array<AnalyticsPixelId>,
  ) {
    this.passedPixelIds = !!pixelIds?.length ? pixelIds : [];
    this.cookieConsentService.addConsentLoadListener(this.onConsentLoad(this.passedPixelIds));
    this.cookieConsentService.addConsentChangeListener(this.onConsentChange(this.passedPixelIds));
  }

  isTfaRequired(): boolean {
    return !!this.passedPixelIds.length;
  }

  isTfaAvailable(): boolean {
    return this.tfa.isAvailable;
  }

  track(name: string, params?: Record<string, unknown>) {
    this.tfa.track(name, params);
  }

  private get tfa(): TaboolaMethods & { isAvailable: boolean } {
    if (!checkIsClientSide() || !window._tfa || !window._taboola) {
      return noop;
    }

    const tfa = window._tfa;
    const taboola = window._taboola;

    return {
      isAvailable: true as const,
      revokeConsentEu: () => taboola.push({ cex: 'false' }),
      revokeConsentUs: () => taboola.push({ cdns: 'true' }),
      grantConsentEu: () => taboola.push({ cex: 'true' }),
      grantConsentUs: () => taboola.push({ cdns: 'false' }),
      track: (name: string, params?: Record<string, unknown>) => {
        this.initializedPixelIds.forEach((id) =>
          tfa.push({ notify: 'event', name, id, ...params }),
        );
      },
    };
  }

  private onConsentLoad =
    (pixels: Array<AnalyticsPixelId>) =>
    (consentState: { TARGETING: boolean; SHARE_OR_SALE_OF_PERSONAL_DATA?: boolean }) => {
      if (consentState.TARGETING) {
        if ('SHARE_OR_SALE_OF_PERSONAL_DATA' in consentState) {
          this.tfa.grantConsentUs();
        } else {
          this.tfa.grantConsentEu();
        }
        this.initializePixels(pixels);
      }
    };

  private onConsentChange =
    (pixels: Array<AnalyticsPixelId>) =>
    (consentState: { TARGETING: boolean; SHARE_OR_SALE_OF_PERSONAL_DATA?: boolean }) => {
      if (consentState.TARGETING) {
        if ('SHARE_OR_SALE_OF_PERSONAL_DATA' in consentState) {
          this.tfa.grantConsentUs();
        } else {
          this.tfa.grantConsentEu();
        }
        this.initializePixels(pixels);
        return;
      }

      if ('SHARE_OR_SALE_OF_PERSONAL_DATA' in consentState) {
        this.tfa.revokeConsentUs();
      } else {
        this.tfa.revokeConsentEu();
      }
    };

  private initializePixels(pixels: Array<AnalyticsPixelId>) {
    if (this.tfa.isAvailable) {
      this.doInitializePixels(pixels);
      return;
    }

    document.addEventListener(
      CustomAppEvents.TABOOLA_PIXEL_LOADED,
      () => {
        this.doInitializePixels(pixels);
      },
      { once: true },
    );
  }

  private doInitializePixels(pixelsToInitialize: Array<AnalyticsPixelId>) {
    pixelsToInitialize.forEach((pixel) => {
      if (this.initializedPixelIds.includes(pixel)) {
        return;
      }

      this.initializedPixelIds.push(pixel);
      this.tfa.track(TaboolaEventName.PAGE_VIEW);
    });
  }
}
